import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API, BASE_URL } from "../api";

import { toasts } from "../components/AppToast";

import Layout from "../components/Layout";

import {
  FiTrash2,
  FiEdit,
  FiSearch,
  FiEye,
  FiX,
  FiFileText,
  FiClipboard,
} from "react-icons/fi";

export default function ManageLocation() {
  const navigate = useNavigate();

  // STATE

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");

  const [page, setPage] = useState(1);
  const PER = 10;

  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [maxSeq, setMaxSeq] = useState(0);

  // View modal
  const [viewLocation, setViewLocation] = useState(null);

  // Delete modal
  const [deleteLocation, setDeleteLocation] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // AUTHENTICATION

  useEffect(() => {
    const token = localStorage.getItem("token");

    if (!token) {
      navigate("/login");
    }
  }, [navigate]);

  // PDF URL

  const getPdfUrl = (attachment) => {
    if (!attachment) {
      return null;
    }

    if (attachment.startsWith("http://") || attachment.startsWith("https://")) {
      return attachment;
    }

    return `${BASE_URL.replace(/\/$/, "")}/${attachment.replace(/^\//, "")}`;
  };

  // FETCH LOCATIONS

  const fetchLocations = useCallback(
    async (currentPage = 1, currentSearch = "") => {
      try {
        setLoading(true);

        const response = await API.get("/locations", {
          params: {
            page: currentPage,
            limit: PER,
            city_id: 0,
            search: currentSearch.trim(),
          },
        });

        if (!response.data?.success) {
          setData([]);
          setTotal(0);
          setTotalPages(1);
          setMaxSeq(0);
          return;
        }

        const locations = response.data?.data || [];

        setData(locations);

        setTotal(Number(response.data?.total || 0));

        setTotalPages(Math.max(Number(response.data?.totalPages || 1), 1));

        setMaxSeq(Number(response.data?.maxSeq || 0));

        // console.log("Location response:", response.data);
        // console.log("Maximum Sequence:", response.data?.maxSeq);
      } catch (error) {
        console.error("Location fetch error:", error);

        setData([]);
        setTotal(0);
        setTotalPages(1);
        setMaxSeq(0);

        toasts.error(
          error?.response?.data?.message ||
            error?.response?.data?.error ||
            "Failed to load locations"
        );
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // INITIAL LOAD / PAGE CHANGE

  useEffect(() => {
    fetchLocations(page, search);
  }, [page, search, fetchLocations]);

  // SEARCH

  const handleSearch = () => {
    setPage(1);
    fetchLocations(1, search);
  };

  // CLEAR SEARCH

  const handleClearSearch = () => {
    setSearch("");
    setPage(1);

    fetchLocations(1, "");
  };

  // SEQUENCE CHANGE

  const handleSequenceChange = async (row, value) => {
    const newSeq = Number(value);
    const currentSeq = Number(row.seq);

    if (!row?.loc_id) {
      toasts.error("Location ID is missing");
      return;
    }

    if (!newSeq || newSeq <= 0) {
      toasts.error("Invalid sequence");
      return;
    }

    // Same sequence - no API call
    if (newSeq === currentSeq) {
      return;
    }

    try {
      setLoading(true);

      const response = await API.put(`/locations/sequence/${row.loc_id}`, {
        seqid: newSeq,
      });

      if (response.data?.success) {
        toasts.success(
          response.data?.message || "Sequence updated successfully"
        );

        await fetchLocations(page, search);
      } else {
        toasts.error(response.data?.message || "Failed to update sequence");
      }
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to update sequence";

      toasts.error(message);
    } finally {
      setLoading(false);
    }
  };

  // DELETE

  const confirmDelete = async () => {
    if (!deleteLocation?.loc_id) {
      return;
    }

    try {
      setDeleting(true);

      await API.delete(`/locations/${deleteLocation.loc_id}`);

      toasts.success("Location deleted successfully");

      setDeleteLocation(null);

      // If last record on current page
      if (data.length === 1 && page > 1) {
        setPage((prev) => prev - 1);
      } else {
        await fetchLocations(page, search);
      }
    } catch (error) {
      console.error("Delete location error:", error);

      toasts.error(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Failed to delete location"
      );
    } finally {
      setDeleting(false);
    }
  };

  // PAGINATION

  const goToPage = (newPage) => {
    if (newPage < 1 || newPage > totalPages || newPage === page) {
      return;
    }

    setPage(newPage);
  };

  // EDIT LOCATION

  const handleEditLocation = (row) => {
    navigate("/manage-location/edit", {
      state: {
        location: row,
      },
    });
  };

  // RENDER

  return (
    <Layout activePage="Location">
      <main className="dashboard-main">
        {/* =====================================================
            SEARCH / HEADER
        ===================================================== */}

        <div className="table-card">
          <div className="table-card-head">
            <h3 className="table-card-title d-flex align-items-center">
              <FiClipboard size={26} className="me-2" />

              <span>Manage Location</span>

              <span className="rec-count ms-1">{total} locations</span>
            </h3>

            {/* SEARCH */}

            <div className="search-wrapper">
              <span className="search-icon">
                <FiSearch />
              </span>

              <input
                type="text"
                placeholder="Search location..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleSearch();
                  }
                }}
                className="search-input"
              />

              {search && (
                <button
                  type="button"
                  className="season-clear-btn"
                  onClick={handleClearSearch}
                >
                  ✕
                </button>
              )}
            </div>

            {/* ADD BUTTON */}

            <button
              type="button"
              className="um-btn-primary"
              onClick={() => navigate("/manage-location/add")}
            >
              <span className="um-add-icon">＋</span>
              Add Location
            </button>
          </div>

          {/* =====================================================
              TABLE
          ===================================================== */}

          <div className="table-responsive">
            <table className="table table-striped table-hover table-bordered align-middle mb-0">
              <thead className="table-light">
                <tr className="um-table-head">
                  <th className="text-center">#</th>

                  <th className="text-center">Location</th>

                  <th className="text-center">Pickup Price</th>

                  <th className="text-center">Drop Price</th>

                  <th className="text-center">Document</th>

                  <th className="text-center">Sequence</th>

                  <th className="text-center">Actions</th>
                </tr>
              </thead>

              <tbody>
                {/* =================================================
                    LOADING
                ================================================= */}

                {loading ? (
                  Array.from({
                    length: 6,
                  }).map((_, i) => (
                    <tr key={i}>
                      {Array.from({
                        length: 7,
                      }).map((_, j) => (
                        <td key={j} className="text-center">
                          <div className="skeleton" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : data.length === 0 ? (
                  /* =================================================
                      EMPTY
                  ================================================= */

                  <tr>
                    <td colSpan={7} className="text-center py-5">
                      <div className="empty-icon">
                        <FiSearch size={18} />
                      </div>

                      <div className="empty-title">No locations found</div>
                    </td>
                  </tr>
                ) : (
                  /* =================================================
                      DATA
                  ================================================= */

                  data.map((row, index) => {
                    const serialNumber = (page - 1) * PER + index + 1;

                    const pdfUrl = getPdfUrl(row.attachment);

                    return (
                      <tr key={row.loc_id} className="um-row">
                        {/* SERIAL */}

                        <td className="text-center">{serialNumber}</td>

                        {/* LOCATION */}

                        <td className="text-center">{row.loc_name || "—"}</td>

                        {/* PICKUP PRICE */}

                        <td className="text-center">
                          ₹ {Number(row.loc_pickupprice || 0).toFixed(2)}
                        </td>

                        {/* DROP PRICE */}

                        <td className="text-center">
                          ₹ {Number(row.loc_dropupprice || 0).toFixed(2)}
                        </td>

                        {/* DOCUMENT */}

                        <td className="text-center">
                          {pdfUrl ? (
                            <a
                              href={pdfUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="action-btn action-view btn btn-sm"
                            >
                              <FiFileText size={15} />
                            </a>
                          ) : (
                            <span className="no-modules-text">No Document</span>
                          )}
                        </td>

                        {/* SEQUENCE */}

                        <td className="text-center">
                          <select
                            className="form-select form-select-sm"
                            value={Number(row.seq) || ""}
                            onChange={(e) =>
                              handleSequenceChange(row, e.target.value)
                            }
                          >
                            <option value="">Select</option>

                            {Array.from(
                              {
                                length: Number(maxSeq) || 0,
                              },
                              (_, i) => i + 1
                            ).map((number) => (
                              <option key={number} value={number}>
                                {String(number).padStart(2, "0")}
                              </option>
                            ))}
                          </select>
                        </td>

                        {/* ACTIONS */}

                        <td>
                          <div className="action-buttons d-flex justify-content-center gap-2">
                            {/* VIEW */}

                            <button
                              type="button"
                              className="action-btn action-view btn btn-sm"
                              title="View"
                              onClick={() => setViewLocation(row)}
                            >
                              <FiEye />
                            </button>

                            {/* EDIT */}

                            <button
                              type="button"
                              className="action-btn action-edit btn btn-sm"
                              title="Edit"
                              onClick={() => handleEditLocation(row)}
                            >
                              <FiEdit />
                            </button>

                            {/* DELETE */}

                            <button
                              type="button"
                              className="action-btn action-delete btn btn-sm"
                              title="Delete"
                              onClick={() => setDeleteLocation(row)}
                            >
                              <FiTrash2 />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>

            {/* =====================================================
                PAGINATION
            ===================================================== */}

            <div className="pagination">
              <span className="pagin-info">
                {total > 0
                  ? `Showing ${(page - 1) * PER + 1}–${Math.min(
                      page * PER,
                      total
                    )} of ${total}`
                  : "No records"}
              </span>

              <div className="d-flex align-items-center gap-2">
                <button
                  type="button"
                  className="pg-btn"
                  disabled={page === 1 || loading}
                  onClick={() => goToPage(page - 1)}
                >
                  ← Prev
                </button>

                <span className="pagin-info">
                  Page {page} / {totalPages || 1}
                </span>

                <button
                  type="button"
                  className="pg-btn"
                  disabled={page >= totalPages || loading}
                  onClick={() => goToPage(page + 1)}
                >
                  Next →
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* =====================================================
          VIEW LOCATION MODAL
      ===================================================== */}

      {viewLocation && (
        <div className="um-overlay" onClick={() => setViewLocation(null)}>
          <div
            className="um-modal um-view-modal"
            onClick={(e) => e.stopPropagation()}
          >
            {/* HEADER */}

            <div className="um-modal-head">
              <h2 className="um-modal-title">Location Details</h2>

              <button
                type="button"
                className="um-x-btn"
                onClick={() => setViewLocation(null)}
              >
                <FiX />
              </button>
            </div>

            {/* BODY */}

            <div className="um-modal-scroll">
              {/* =================================================
                  BASIC INFORMATION
              ================================================= */}

              <div className="sec-div">
                <span>BASIC INFORMATION</span>
              </div>

              <div className="row g-3">
                {/* LOCATION */}

                <div className="col-xl-4 col-lg-4 col-md-6 col-12">
                  <div className="profile-row">
                    <div>
                      <div className="form-label">Location</div>

                      <div className="profile-row-value">
                        {viewLocation.loc_name || "—"}
                      </div>
                    </div>
                  </div>
                </div>

                {/* PICKUP */}

                <div className="col-xl-4 col-lg-4 col-md-6 col-12">
                  <div className="profile-row">
                    <div>
                      <div className="form-label">Pickup Price</div>

                      <div className="profile-row-value">
                        ₹ {Number(viewLocation.loc_pickupprice || 0).toFixed(2)}
                      </div>
                    </div>
                  </div>
                </div>

                {/* DROP */}

                <div className="col-xl-4 col-lg-4 col-md-6 col-12">
                  <div className="profile-row">
                    <div>
                      <div className="form-label">Drop Price</div>

                      <div className="profile-row-value">
                        ₹ {Number(viewLocation.loc_dropupprice || 0).toFixed(2)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* =================================================
                  VOUCHER INFORMATION
              ================================================= */}

              <div className="sec-div">
                <span>VOUCHER INFORMATION</span>
              </div>

              <div className="profile-row">
                <div
                  className="profile-row-value"
                  dangerouslySetInnerHTML={{
                    __html: viewLocation.loc_voucherinfo || "—",
                  }}
                />
              </div>

              {/* =================================================
                  EXTRA INFORMATION
              ================================================= */}

              <div className="sec-div">
                <span>EXTRA INFORMATION</span>
              </div>

              <div className="profile-row">
                <div
                  className="profile-row-value"
                  dangerouslySetInnerHTML={{
                    __html: viewLocation.loc_extrainfo || "—",
                  }}
                />
              </div>

              {/* =================================================
                  DOCUMENT
              ================================================= */}

              <div className="sec-div">
                <span>DOCUMENT</span>
              </div>

              <div className="profile-row">
                <div className="form-label">PDF Document</div>

                <div className="mt-2">
                  {getPdfUrl(viewLocation.attachment) ? (
                    <a
                      href={getPdfUrl(viewLocation.attachment)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="um-btn-primary d-inline-flex align-items-center gap-2"
                    >
                      <FiFileText />
                      View PDF Document
                    </a>
                  ) : (
                    <span className="no-modules-text">
                      No document available
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* FOOTER */}

            <div className="um-modal-foot">
              <button
                type="button"
                className="um-btn-secondary"
                onClick={() => setViewLocation(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* =====================================================
          DELETE LOCATION MODAL
      ===================================================== */}

      {deleteLocation && (
        <div
          className="um-overlay"
          onClick={() => !deleting && setDeleteLocation(null)}
        >
          <div
            className="um-modal um-delete-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="delete-content">
              {/* DELETE ICON */}

              <div className="delete-icon">
                <FiTrash2 />
              </div>

              {/* TITLE */}

              <h2 className="delete-title">Delete Location?</h2>

              {/* DESCRIPTION */}

              <p className="delete-description">
                Are you sure you want to delete{" "}
                <strong>{deleteLocation.loc_name || "this location"}</strong>
                ?
                <br />
                This action cannot be undone.
              </p>

              {/* ACTIONS */}

              <div className="delete-actions">
                <button
                  type="button"
                  className="um-btn-secondary"
                  disabled={deleting}
                  onClick={() => setDeleteLocation(null)}
                >
                  Cancel
                </button>

                <button
                  type="button"
                  className="delete-confirm-btn"
                  disabled={deleting}
                  onClick={confirmDelete}
                >
                  {deleting ? (
                    <span className="spinner" />
                  ) : (
                    <>
                      <FiTrash2 />
                      Delete Permanently
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
